import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  CI_ROLE_NAMES,
  type CiRoleName,
  isCiRoleName,
} from "../../shared/permissions";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { EmptyState, ErrorBanner, Loading } from "../components/Feedback";
import { authClient, type CiSession } from "../lib/auth-client";
import { formatDateTime, formatRelative, pluralize } from "../lib/format";

type UsersPageProps = {
  /** Signed-in session */
  session: CiSession;
};

const PAGE_SIZE = 50;

const ROLE_HELP: Record<CiRoleName, string> = {
  viewer: "Reads everything. Everyone in the tenant starts here.",
  editor: "Also creates, edits and deletes API mocks.",
  admin: "Also manages users.",
};

/** Unwraps a Better Auth client result, throwing its error. */
const unwrap = <T,>(result: {
  data: T | null;
  error: { message?: string } | null;
}): T => {
  if (result.error || result.data === null) {
    throw new Error(result.error?.message ?? "Request failed");
  }
  return result.data;
};

/** Everyone who has signed in, with their role, ban and sessions (admins only). */
export const UsersPage = ({ session }: UsersPageProps) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const users = useQuery({
    queryKey: ["users", search, offset],
    keepPreviousData: true,
    queryFn: async () =>
      unwrap(
        await authClient.admin.listUsers({
          query: {
            limit: PAGE_SIZE,
            offset,
            sortBy: "createdAt",
            sortDirection: "desc",
            ...(search
              ? {
                  searchValue: search,
                  searchField: "email",
                  searchOperator: "contains",
                }
              : {}),
          },
        }),
      ),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["users"] });
  const setRole = useMutation({
    mutationFn: async ({
      userId,
      role,
    }: {
      userId: string;
      role: CiRoleName;
    }) => unwrap(await authClient.admin.setRole({ userId, role })),
    onSuccess: refresh,
  });
  const setBanned = useMutation({
    mutationFn: async ({
      userId,
      banned,
    }: {
      userId: string;
      banned: boolean;
    }) =>
      banned
        ? unwrap(
            await authClient.admin.banUser({
              userId,
              banReason: `Banned by ${session.user.email}`,
            }),
          )
        : unwrap(await authClient.admin.unbanUser({ userId })),
    onSuccess: refresh,
  });
  const revoke = useMutation({
    mutationFn: async (userId: string) =>
      unwrap(await authClient.admin.revokeUserSessions({ userId })),
  });
  const total = users.data?.total ?? 0;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Users</h1>
        <p className="text-sm text-slate-500">
          Anyone in the Microsoft Entra tenant can sign in and starts as a
          viewer.
        </p>
      </div>
      <div className="card grid gap-3 px-4 py-3 sm:grid-cols-3">
        {CI_ROLE_NAMES.map((role) => (
          <div key={role} className="text-sm">
            <Badge tone="violet">{role}</Badge>{" "}
            <span className="text-slate-600">{ROLE_HELP[role]}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <input
          className="input max-w-xs"
          type="search"
          placeholder="Search by email"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setOffset(0);
          }}
        />
        <span className="ml-auto text-sm text-slate-500">
          {pluralize(total, "user")}
        </span>
      </div>
      <ErrorBanner
        error={users.error ?? setRole.error ?? setBanned.error ?? revoke.error}
      />
      <div className="card overflow-x-auto">
        {users.isLoading ? (
          <Loading />
        ) : !users.data?.users.length ? (
          <EmptyState title="No users match" />
        ) : (
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="table-head">User</th>
                <th className="table-head">Role</th>
                <th className="table-head">Joined</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.data.users.map((user) => {
                const isSelf = user.id === session.user.id;
                const role = isCiRoleName(user.role) ? user.role : "viewer";
                return (
                  <tr key={user.id}>
                    <td className="table-cell">
                      <div className="font-medium">{user.name}</div>
                      <div className="text-xs text-slate-500">{user.email}</div>
                    </td>
                    <td className="table-cell">
                      <select
                        className="input w-32 py-1"
                        value={role}
                        disabled={isSelf || setRole.isLoading}
                        title={
                          isSelf ? "You can't change your own role" : undefined
                        }
                        aria-label={`Role of ${user.email}`}
                        onChange={(event) =>
                          setRole.mutate({
                            userId: user.id,
                            role: event.target.value as CiRoleName,
                          })
                        }
                      >
                        {CI_ROLE_NAMES.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td
                      className="table-cell text-slate-600"
                      title={formatDateTime(String(user.createdAt))}
                    >
                      {formatRelative(new Date(user.createdAt).toISOString())}
                    </td>
                    <td className="table-cell">
                      {user.banned ? (
                        <Badge tone="rose">banned</Badge>
                      ) : (
                        <Badge tone="green">active</Badge>
                      )}
                    </td>
                    <td className="table-cell whitespace-nowrap text-right">
                      {isSelf ? null : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={revoke.isLoading}
                            onClick={() => revoke.mutate(user.id)}
                          >
                            Sign out everywhere
                          </Button>
                          <Button
                            variant={user.banned ? "secondary" : "ghost"}
                            size="sm"
                            disabled={setBanned.isLoading}
                            onClick={() =>
                              (user.banned ||
                                window.confirm(
                                  `Ban ${user.email}? They are signed out and can't sign in again.`,
                                )) &&
                              setBanned.mutate({
                                userId: user.id,
                                banned: !user.banned,
                              })
                            }
                          >
                            {user.banned ? "Unban" : "Ban"}
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <span className="text-slate-500">
            {offset + 1}–{Math.min(total, offset + PAGE_SIZE)} of {total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      ) : null}
    </div>
  );
};
